type CultureTagStatus = 'default' | 'highlight' | 'positive' | 'helper' | 'negative';

interface CultureTag {
  /** Código de duas letras exibido no Tag (ex.: `BR`, `EN`). */
  readonly label: string;
  /** Status visual do Tag do design system associado à cultura. */
  readonly status: CultureTagStatus;
}

const CULTURE_TAGS: Record<string, CultureTag> = {
  'pt-BR': { label: 'BR', status: 'positive' },
  'en-US': { label: 'EN', status: 'highlight' },
  'es-ES': { label: 'ES', status: 'helper' },
  'fr-FR': { label: 'FR', status: 'negative' },
};

/**
 * Mapeia um código de cultura BCP-47 para o `label` e `status` de um `Tag` do
 * design system, centralizando a relação cultura → código/cor compartilhada
 * entre as telas de idiomas (exam-management e exam-revision).
 *
 * Culturas não mapeadas caem para as duas primeiras letras em maiúsculo com
 * status neutro (`default`).
 *
 * @param cultureInfo - Código de cultura BCP-47 (ex.: `pt-BR`)
 * @returns `label` e `status` para uso direto no `Tag` do `@ads/components-react`
 *
 * @example
 * ```tsx
 * const { label, status } = getCultureTag('pt-BR');
 * <Tag label={label} status={status} size="sm" />
 * ```
 */
export function getCultureTag(cultureInfo: string): CultureTag {
  return (
    CULTURE_TAGS[cultureInfo] ?? {
      label: cultureInfo.slice(0, 2).toUpperCase(),
      status: 'default',
    }
  );
}
